import {
    Component,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { MatButtonToggleChange, MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { EventsPaginatedService } from '../../services/events-paginated.service';
import { UserRoles } from 'api/models';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { UsersService } from 'api/services';
import { lastValueFrom } from 'rxjs';
import { EventsStore } from '@app/stores/eventsStore';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-overview-buttons',
    templateUrl: './overview-buttons.component.html',
    styleUrls: ['./overview-buttons.component.scss'],
    imports: [
        MatButtonToggleGroup,
        MatButtonToggle,
        MatIcon,
        TranslateModule,
    ],
})
export class OverviewButtonsComponent implements OnInit {
    selectedView = output<string>();
    viewTypeInput = input<string>('folder', { alias: 'viewType' });

    /** Effective view type, seeded from input and updated on toggle change. */
    viewType = signal<string>('folder');

    private eventsStore = inject(EventsStore);

    constructor(
        private eventsPaginatedService: EventsPaginatedService,
        private sessionService: SessionService,
        private usersService: UsersService
    ) {
        effect(() => {
            this.viewType.set(this.viewTypeInput());
        });
    }
    hasSelfServiceAccess = signal(false);

    allowedRoles = [
        UserRoles.Carrier,
        UserRoles.Consignee,
        UserRoles.Consignor,
        UserRoles.OrganizationAdmin,
        UserRoles.SiteAdmin,
        UserRoles.SelfCheckin,
    ];

    async ngOnInit(): Promise<void> {
        await this.hasSelfService();
    }

    /** Reads the newNotifications signal from eventsStore. */
    get hasNewNotifications(): boolean {
        return this.hasSelfServiceAccess() && this.eventsStore.newNotifications();
    }

    /**
     * Changes the view type based on the button toggle change event.
     * If the 'notifications' view is selected, it sets new notifications to false.
     * @param event The button toggle change event.
     */
    changeView(event: MatButtonToggleChange): void {
        this.selectedView.emit(event.value);
        this.viewType.set(event.value);
        if (event.value === 'notifications') {
            this.eventsPaginatedService.setNewNotificationsToFalse();
        }
    }

    /**
     * Checks if the user has self-service check-in access.
     * Retrieves the organization sites and filters them based on user roles.
     * Sets the `hasSelfServiceAccess` property based on the presence of self-checkin enabled sites.
     */
    async hasSelfService() {
        const hasSelfCheckin = this.sessionService
            .getMyOrganizationSites()
            .filter(allSite =>
                allSite.roles!.some((role: UserRoles) =>
                    this.allowedRoles.includes(role)
                )
            )
            .some(allSite => allSite.selfCheckin);
        this.hasSelfServiceAccess.set(hasSelfCheckin);
    }
}
